import type { LanguageCode } from '@/config/languages'

/** Public menu app base URL per admin deployment environment */
const DEFAULT_MENU_DOMAIN_BY_ENVIRONMENT = {
  LOCAL: 'http://localhost:3000',
  DEVELOPMENT: 'https://menu.menupoints.com.tr',
  PRODUCTION: 'https://menu.menupoints.com.tr',
} as const

type MenuEnvironment = keyof typeof DEFAULT_MENU_DOMAIN_BY_ENVIRONMENT

function normalizeMenuDomainBase(value: string): string {
  return value.trim().replace(/\/$/, '')
}

function readViteEnv(key: string): string | undefined {
  const env = import.meta.env as ImportMetaEnv | undefined
  if (!env) return undefined
  const raw = env[key as keyof ImportMetaEnv]
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined
}

type ResolveMenuDomainOptions = {
  environment?: string
  menuDomain?: string
  menuDomainLocal?: string
}

/** Resolves the customer menu app origin from VITE_ENVIRONMENT and optional overrides. */
export function resolveMenuDomain(options?: ResolveMenuDomainOptions): string {
  const environment = (options?.environment ??
    readViteEnv('VITE_ENVIRONMENT') ??
    'PRODUCTION') as MenuEnvironment

  if (environment === 'LOCAL') {
    const localOverride = options?.menuDomainLocal ?? readViteEnv('VITE_MENU_DOMAIN_LOCAL')
    if (localOverride) return normalizeMenuDomainBase(localOverride)
    return DEFAULT_MENU_DOMAIN_BY_ENVIRONMENT.LOCAL
  }

  const explicit = options?.menuDomain ?? readViteEnv('VITE_MENU_DOMAIN')
  if (explicit) return normalizeMenuDomainBase(explicit)

  const fallback =
    environment in DEFAULT_MENU_DOMAIN_BY_ENVIRONMENT
      ? DEFAULT_MENU_DOMAIN_BY_ENVIRONMENT[environment]
      : DEFAULT_MENU_DOMAIN_BY_ENVIRONMENT.PRODUCTION

  return fallback
}

export function buildMenuUrl(
  lang: LanguageCode,
  slug: string,
  menuDomain = resolveMenuDomain()
): string {
  const base = menuDomain.replace(/\/$/, '')
  const path = `${lang}/${slug}`.replace(/^\/+/, '')
  return `${base}/${path}`
}

export function getMenuDomainHost(menuDomain = resolveMenuDomain()): string {
  if (!menuDomain) return ''
  try {
    return new URL(menuDomain).host
  } catch {
    return menuDomain.replace(/^https?:\/\//, '').replace(/\/$/, '')
  }
}
